import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Globe, HelpCircle, Moon, Sun } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Internationalization } from "@/components/language/internationalization";
import UserLearnLanguagesDropdown from "@/components/language/user-learn-languages";
import { useLearnLanguage } from "@/context/learn-language-context";
import { useLocation } from "react-router";
import { useTheme } from "@/components/theme-provider";
import { useTranslation } from "react-i18next";
import { useVoiceChatTour } from "@/hooks/useVoiceChatTour";

type TopNavProps = {
  hasLanguageSelector: boolean;
};

export function TopNavComponent({ hasLanguageSelector = false }: TopNavProps) {
  const { i18n, t } = useTranslation(["pages", "common"]);
  const { setTheme, theme } = useTheme();
  const { languageProficiency } = useLearnLanguage();
  const location = useLocation();

  // Access specific properties
  const { pathname } = location;

  // Tour
  const { startTour } = useVoiceChatTour();

  return (
    <nav className="p-4 md:p-4 fixed top-0 left-0 right-0 flex items-center justify-between align-middle bg-background">
      <div className="min-w-14" />
      {hasLanguageSelector && (
        <div className="flex-grow max-w-xs sm:max-w-md md:max-w-lg lg:max-w-xl mx-4">
          <UserLearnLanguagesDropdown
            value={
              languageProficiency &&
              `${languageProficiency.language.key}-${languageProficiency.proficiency}`
            }
          />
        </div>
      )}

      <div className="flex flex-row items-center justify-between md:space-x-2 ml-2">
        {pathname === "/app/voicechat" && (
          <Button
            onClick={startTour}
            variant="secondary"
            size="sm"
            aria-label={t("pages:voicechatTour.startTour")}
          >
            <HelpCircle className="h-4 w-4" />
            <span className="hidden sm:inline">
              {t("pages:voicechatTour.startTour")}
            </span>
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="p-2">
              {i18n.language.toUpperCase()}
              <Globe className="hidden sm:inline h-5 w-5" />
              <span className="sr-only">Select language</span>
            </Button>
          </DropdownMenuTrigger>
          <Internationalization />
        </DropdownMenu>

        <Button
          variant="ghost"
          size="sm"
          className="p-2"
          onClick={() => setTheme(theme === "light" ? "dark" : "light")}
        >
          <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
          <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          <span className="sr-only">Toggle theme</span>
        </Button>
      </div>
    </nav>
  );
}
